export interface PasswordPolicyResult {
  isValid: boolean;
  errors: string[];
  requirements: {
    minLength: boolean;
    hasSpecialChar: boolean;
  };
}

export interface PasswordPolicyConfig {
  minLength: number;
  requireSpecialChar: boolean;
}

export const DEFAULT_PASSWORD_POLICY: PasswordPolicyConfig = {
  minLength: 12,
  requireSpecialChar: true,
} as const;

export const PASSWORD_POLICY = {
  SPECIAL_CHARS: /[!@#$%^&*(),.?":{}|<>]/,
} as const;

/**
 * Validates password against the security policy
 * @param password - The password to validate
 * @param config - Password policy configuration (optional, uses default if not provided)
 * @returns PasswordPolicyResult with validation results
 */
export function validatePasswordPolicy(password: string, config: PasswordPolicyConfig = DEFAULT_PASSWORD_POLICY): PasswordPolicyResult {
  const errors: string[] = [];
  const requirements = {
    minLength: password.length >= config.minLength,
    hasSpecialChar: config.requireSpecialChar ? PASSWORD_POLICY.SPECIAL_CHARS.test(password) : true,
  };

  if (!requirements.minLength) {
    errors.push(`อย่างน้อย ${config.minLength} ตัวอักษร`);
  }

  if (config.requireSpecialChar && !requirements.hasSpecialChar) {
    errors.push('ต้องมีอักขระพิเศษอย่างน้อย 1 ตัว');
  }

  return {
    isValid: errors.length === 0,
    errors,
    requirements,
  };
}

/**
 * Gets password strength score (0-100)
 * @param password - The password to evaluate
 * @returns Strength score
 */
export function getPasswordStrength(password: string): number {
  let score = 0;
  const checks = [
    password.length >= 8, // Basic length
    password.length >= 12, // Policy length
    password.length >= 16, // Strong length
    /[a-z]/.test(password), // Lowercase
    /[A-Z]/.test(password), // Uppercase
    /[0-9]/.test(password), // Numbers
    PASSWORD_POLICY.SPECIAL_CHARS.test(password), // Special chars
    /[!@#$%^&*(),.?":{}|<>]{2,}/.test(password), // Multiple special chars
  ];

  checks.forEach((check) => {
    if (check) score += 12.5;
  });

  return Math.min(score, 100);
}

/**
 * Gets password strength level based on score
 * @param score - Password strength score (0-100)
 * @returns Strength level
 */
export function getPasswordStrengthLevel(score: number): 'weak' | 'medium' | 'strong' | 'very-strong' {
  if (score < 40) return 'weak';
  if (score < 60) return 'medium';
  if (score < 80) return 'strong';
  return 'very-strong';
}

/**
 * Gets password strength color for UI
 * @param level - Password strength level
 * @returns Color hex code
 */
export function getPasswordStrengthColor(level: string): string {
  switch (level) {
    case 'weak': return '#f44336';
    case 'medium': return '#ff9800';
    case 'strong': return '#4caf50';
    case 'very-strong': return '#2196f3';
    default: return '#9e9e9e';
  }
}

/**
 * Gets password strength text for display
 * @param level - Password strength level
 * @returns Localized strength text
 */
export function getPasswordStrengthText(level: string): string {
  switch (level) {
    case 'weak': return 'อ่อนแอ';
    case 'medium': return 'ปานกลาง';
    case 'strong': return 'แข็งแกร่ง';
    case 'very-strong': return 'แข็งแกร่งมาก';
    default: return '';
  }
}
